import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Sprout, 
  ArrowLeft, 
  Upload, 
  Image as ImageIcon, 
  X, 
  CheckCircle2, 
  Building2, 
  Calendar, 
  Users, 
  Check, 
  Gift, 
  Truck,
  Trash2,
  Loader2
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import LocationSearchInput from '../../components/LocationSearchInput';

interface ExistingImage {
  image_id: number;
  image_url: string;
}

const EditFarmProject: React.FC = () => {
  const navigate = useNavigate();
  const { farm_project_id } = useParams<{ farm_project_id: string }>();
  const token = localStorage.getItem('diasporawise_admin_token') || localStorage.getItem('token');

  // Loading & Initial Fetch State
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Identifiers
  const [projectId, setProjectId] = useState<number | null>(null);

  // Base Project Form Fields
  const [title, setTitle] = useState('');
  const [shortDesc, setShortDesc] = useState('');
  const [fullDesc, setFullDesc] = useState('');
  const [location, setLocation] = useState('');
  const [locationLat, setLocationLat] = useState(0);
  const [locationLon, setLocationLon] = useState(0);
  const [fundingGoal, setFundingGoal] = useState('');

  // Farm Sponsorship Specifications
  const [capacity, setCapacity] = useState('150');
  const [estimatedHarvest, setEstimatedHarvest] = useState('');
  const [benefitOptions, setBenefitOptions] = useState<('produce' | 'rita_credit')[]>(['produce', 'rita_credit']);
  const [maxBeneficiaries, setMaxBeneficiaries] = useState('5');
  const [isSponsorshipOpen, setIsSponsorshipOpen] = useState(true);

  // Existing Images & New Images Upload State
  const [existingImages, setExistingImages] = useState<ExistingImage[]>([]);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null);

  // Submission Status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Helper to format image URLs
  const getImageUrl = (url: string | null | undefined) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // If the image is hosted on production but we are running locally, redirect to local backend so local uploads work
      if (API_BASE_URL.includes('localhost') && url.includes('diasporawise.com')) {
        return url.replace(/https?:\/\/diasporawise\.com(:\d+)?/, API_BASE_URL);
      }
      return url;
    }
    if (url.startsWith('/uploads')) {
      return `${API_BASE_URL}${url}`;
    }
    return `${API_BASE_URL}/uploads/${url}`;
  };

  // Fetch Farm Project & Base Project Details on Mount
  useEffect(() => {
    if (!token) {
      setFetchError('Admin authentication token missing.');
      setLoading(false);
      return;
    }

    if (!farm_project_id) {
      setFetchError('Invalid Farm Project ID.');
      setLoading(false);
      return;
    }

    const loadFarmData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/admin/farm-operations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const payload = await res.json();
        if (!payload.success) throw new Error(payload.message || 'Failed to fetch farm data.');

        const farms: any[] = payload.data?.farms || [];
        const currentFarm = farms.find((f: any) => String(f.farm_project_id) === String(farm_project_id));

        if (!currentFarm) {
          throw new Error(`Farm Project #${farm_project_id} not found.`);
        }

        // Populate Farm Specs
        setCapacity(String(currentFarm.sponsorship_capacity || 150));
        setEstimatedHarvest(
          currentFarm.estimated_harvest_at
            ? new Date(currentFarm.estimated_harvest_at).toISOString().split('T')[0]
            : ''
        );
        setBenefitOptions(currentFarm.benefit_options || ['produce', 'rita_credit']);
        setMaxBeneficiaries(String(currentFarm.max_beneficiaries_per_sponsorship || 5));
        setIsSponsorshipOpen(currentFarm.is_sponsorship_open ?? true);

        // Populate Base Project Info
        const proj = currentFarm.project;
        if (proj) {
          setProjectId(proj.project_id);
          setTitle(proj.title || '');
          setShortDesc(proj.short_description || '');
          setFullDesc(proj.full_description || '');
          setLocation(proj.location || '');
          setLocationLat(proj.latitude || 0);
          setLocationLon(proj.longitude || 0);
          setFundingGoal(proj.funding_goal ? String(proj.funding_goal) : '');

          if (Array.isArray(proj.images)) {
            setExistingImages(
              proj.images.map((img: any) => ({
                image_id: img.image_id || img.project_images_id,
                image_url: img.image_url
              }))
            );
          }
        }
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Unable to load farm project details.');
      } finally {
        setLoading(false);
      }
    };

    loadFarmData();
  }, [farm_project_id, token]);

  // Handle New Image File Selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).filter((file) => file.type.startsWith('image/'));
    setSelectedImages((prev) => [...prev, ...files]);

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeSelectedImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle Existing Image Deletion
  const handleDeleteExistingImage = async (imageId: number) => {
    if (!token || !imageId || !projectId) {
      setFeedback({ type: 'error', text: 'Missing project or image identifier for deletion.' });
      return;
    }
    setDeletingImageId(imageId);

    try {
      const res = await fetch(`${API_BASE_URL}/admin/delete-project-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image_id: imageId, project_id: projectId })
      });
      const payload = await res.json();
      if (!payload.success) throw new Error(payload.message || 'Failed to delete image.');

      setExistingImages((prev) => prev.filter((img) => img.image_id !== imageId));
      setFeedback({ type: 'success', text: 'Project image deleted successfully.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err instanceof Error ? err.message : 'Could not delete project image.'
      });
    } finally {
      setDeletingImageId(null);
    }
  };

  const toggleBenefitOption = (opt: 'produce' | 'rita_credit') => {
    if (benefitOptions.includes(opt)) {
      if (benefitOptions.length > 1) {
        setBenefitOptions(benefitOptions.filter((o) => o !== opt));
      }
    } else {
      setBenefitOptions([...benefitOptions, opt]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setFeedback({ type: 'error', text: 'Admin authentication missing. Please login again.' });
      return;
    }

    if (!farm_project_id) return;

    setIsSubmitting(true);
    setFeedback(null);

    try {
      // STEP 1: Update Base Project Info if project_id exists
      if (projectId) {
        const updateProjRes = await fetch(`${API_BASE_URL}/admin/update-project`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            project_id: projectId,
            title: title.trim(),
            short_description: shortDesc.trim(),
            full_description: fullDesc.trim(),
            location: location.trim(),
            latitude: locationLat || 7.37,
            longitude: locationLon || 3.94,
            funding_goal: Number(fundingGoal) || 15000000
          })
        });

        const projPayload = await updateProjRes.json();
        if (!projPayload.success) {
          console.warn('Base project update warning:', projPayload.message);
        }
      }

      // STEP 2: Upload New Images if selected
      if (selectedImages.length > 0 && projectId) {
        try {
          const formData = new FormData();
          formData.append('project_id', String(projectId));
          selectedImages.forEach((img) => formData.append('images', img));

          await fetch(`${API_BASE_URL}/admin/add-project-images`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData
          });
        } catch (imgErr) {
          console.error('Image upload warning:', imgErr);
        }
      }

      // STEP 3: Update Farm Project Parameters
      const farmRes = await fetch(`${API_BASE_URL}/admin/farm-projects/${farm_project_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sponsorship_capacity: Number(capacity) || 150,
          benefit_options: benefitOptions,
          max_beneficiaries_per_sponsorship: Number(maxBeneficiaries) || 5,
          estimated_harvest_at: estimatedHarvest ? new Date(estimatedHarvest).toISOString() : null,
          is_sponsorship_open: isSponsorshipOpen
        })
      });

      const farmPayload = await farmRes.json();
      if (!farmPayload.success) {
        throw new Error(farmPayload.message || 'Failed to update farm project parameters.');
      }

      setFeedback({ type: 'success', text: 'Farm Project updated successfully!' });

      setTimeout(() => {
        navigate('/admin/farm-operations');
      }, 1400);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err instanceof Error ? err.message : 'An error occurred while updating farm project.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-gray-600 font-bold text-sm">Loading Farm Project Details...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md max-w-md w-full text-center space-y-4">
          <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto">
            <X className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Farm Project Not Found</h2>
          <p className="text-xs text-gray-500">{fetchError}</p>
          <button
            onClick={() => navigate('/admin/farm-operations')}
            className="px-5 py-2.5 bg-emerald-600 text-white font-bold text-xs rounded-xl"
          >
            Back to Console
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Top Header & Breadcrumb */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/farm-operations')}
            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-all text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-extrabold text-xs rounded-full uppercase flex items-center gap-1">
                <Sprout className="w-3.5 h-3.5" /> Farm Project #{farm_project_id}
              </span>
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900 mt-1">Edit Farm Project</h1>
          </div>
        </div>

        <button
          onClick={() => navigate('/admin/farm-operations')}
          className="px-4 py-2 text-xs font-bold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl"
        >
          Cancel & Back to Console
        </button>
      </div>

      {feedback && (
        <div
          className={`p-4 rounded-xl text-sm font-bold flex items-center gap-2 ${
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          ) : (
            <X className="w-5 h-5 text-red-600" />
          )}
          <span>{feedback.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SECTION 1: BASE PROJECT DETAILS */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" /> 1. Farm Project Information
          </h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-extrabold text-gray-700 mb-1">Farm Project Title *:</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Oyo State Layer Poultry & Egg Farm 2026"
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <LocationSearchInput
                label="Location *:"
                value={location}
                required
                placeholder="Search location..."
                onChange={(loc) => {
                  setLocation(loc.label);
                  setLocationLat(loc.lat);
                  setLocationLon(loc.lon);
                }}
              />
            </div>

            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-1">Funding Goal (₦) *:</label>
              <input
                type="number"
                required
                value={fundingGoal}
                onChange={(e) => setFundingGoal(e.target.value)}
                placeholder="e.g. 15000000"
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-extrabold text-gray-700 mb-1">Short Description:</label>
              <input
                type="text"
                value={shortDesc}
                onChange={(e) => setShortDesc(e.target.value)}
                placeholder="e.g. High-yield 10,000-layer automated poultry egg and broiler farm in Ibadan."
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-extrabold text-gray-700 mb-1">Full Description:</label>
              <textarea
                rows={3}
                value={fullDesc}
                onChange={(e) => setFullDesc(e.target.value)}
                placeholder="Provide full operational breakdown of the farm project..."
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: FARM SPONSORSHIP SPECIFICATIONS */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Sprout className="w-5 h-5 text-emerald-600" /> 2. Farm Sponsorship Parameters & Capacity
          </h3>

          <div className="grid sm:grid-cols-3 gap-4 text-xs font-bold">
            <div>
              <label className="block text-gray-700 mb-1">Sponsorship Limit (Capacity Units) *:</label>
              <input
                type="number"
                required
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="e.g. 150 Units"
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-400 mt-1">Total slots available for diaspora sponsors.</p>
            </div>

            <div>
              <label className="block text-gray-700 mb-1">Estimated Harvest Date:</label>
              <input
                type="date"
                value={estimatedHarvest}
                onChange={(e) => setEstimatedHarvest(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-400 mt-1">Expected harvest completion date.</p>
            </div>

            <div>
              <label className="block text-gray-700 mb-1">Max Beneficiaries per Sponsorship:</label>
              <input
                type="number"
                min={1}
                max={10}
                value={maxBeneficiaries}
                onChange={(e) => setMaxBeneficiaries(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-400 mt-1">Max nominated family members per sponsor.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 pt-2">
            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-2">Allowed Beneficiary Benefit Options:</label>
              <div className="flex flex-wrap gap-4">
                <label
                  onClick={() => toggleBenefitOption('produce')}
                  className={`cursor-pointer px-4 py-3 rounded-xl border-2 flex items-center gap-2 text-xs font-extrabold transition-all ${
                    benefitOptions.includes('produce')
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                      : 'border-gray-200 text-gray-500'
                  }`}
                >
                  <Truck className="w-4 h-4 text-emerald-600" />
                  <span>Doorstep Produce Delivery</span>
                  {benefitOptions.includes('produce') && <Check className="w-4 h-4 text-emerald-600 ml-1" />}
                </label>

                <label
                  onClick={() => toggleBenefitOption('rita_credit')}
                  className={`cursor-pointer px-4 py-3 rounded-xl border-2 flex items-center gap-2 text-xs font-extrabold transition-all ${
                    benefitOptions.includes('rita_credit')
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                      : 'border-gray-200 text-gray-500'
                  }`}
                >
                  <Gift className="w-4 h-4 text-emerald-600" />
                  <span>Rita.ng Shopping Credit</span>
                  {benefitOptions.includes('rita_credit') && <Check className="w-4 h-4 text-emerald-600 ml-1" />}
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-2">Sponsorship Status:</label>
              <select
                value={isSponsorshipOpen ? 'open' : 'closed'}
                onChange={(e) => setIsSponsorshipOpen(e.target.value === 'open')}
                className="w-full p-3 border border-gray-300 rounded-xl text-sm font-semibold bg-white"
              >
                <option value="open">OPEN for Sponsorship</option>
                <option value="closed">CLOSED</option>
              </select>
            </div>
          </div>
        </div>

        {/* SECTION 3: IMAGE MANAGEMENT SECTION */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-emerald-600" /> 3. Farm Project Cover & Photos
          </h3>

          {/* Existing Photos Grid */}
          {existingImages.length > 0 && (
            <div className="space-y-2">
              <label className="block text-xs font-extrabold text-gray-700">Existing Uploaded Photos:</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {existingImages.map((img) => (
                  <div key={img.image_id} className="relative group rounded-xl overflow-hidden border border-gray-200 h-32 bg-gray-100">
                    <img src={getImageUrl(img.image_url)} alt="Farm Project" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      disabled={deletingImageId === img.image_id}
                      onClick={() => handleDeleteExistingImage(img.image_id)}
                      className="absolute top-2 right-2 p-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full shadow-md transition-all disabled:opacity-50"
                      title="Delete this photo"
                    >
                      {deletingImageId === img.image_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Photos Upload */}
          <div className="space-y-2 pt-2">
            <label className="block text-xs font-extrabold text-gray-700">Upload Additional Farm Photos:</label>
            <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-emerald-400 transition-all bg-emerald-50/20">
              <input
                type="file"
                id="edit-farm-images"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <label htmlFor="edit-farm-images" className="cursor-pointer space-y-2 block">
                <Upload className="w-8 h-8 text-emerald-600 mx-auto animate-bounce" />
                <p className="font-extrabold text-sm text-gray-800">Click to Upload New Photos</p>
                <p className="text-xs text-gray-400">Supports PNG, JPG, JPEG, WebP images</p>
              </label>
            </div>

            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
                {imagePreviews.map((src, idx) => (
                  <div key={idx} className="relative group rounded-xl overflow-hidden border border-emerald-300 h-28 bg-gray-100">
                    <img src={src} alt={`New Preview ${idx}`} className="w-full h-full object-cover" />
                    <span className="absolute top-1 left-1 px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-extrabold rounded-full">
                      New
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSelectedImage(idx)}
                      className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full opacity-80 hover:opacity-100 transition-all"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/farm-operations')}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-bold rounded-xl text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-xl shadow-md transition-all text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Updating Farm Project...
              </>
            ) : (
              <>
                <Sprout className="w-5 h-5" /> Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditFarmProject;
